import { clamp } from '../core/math';
import { createCoverage, makeRandom, type CoverageBitmap } from './tip';
import type { TipImage } from './types';

const TIP_SIZE = 128;

/** Lattice value noise with bilinear interpolation, in 0..1. */
function valueNoise(random: () => number, cells: number): (x: number, y: number) => number {
  const stride = cells + 1;
  const lattice = new Float32Array(stride * stride);
  for (let i = 0; i < lattice.length; i += 1) lattice[i] = random();

  return (x, y) => {
    const fx = clamp(x, 0, 1) * cells;
    const fy = clamp(y, 0, 1) * cells;
    const x0 = Math.min(cells - 1, Math.floor(fx));
    const y0 = Math.min(cells - 1, Math.floor(fy));
    const tx = fx - x0;
    const ty = fy - y0;
    const top = lattice[y0 * stride + x0] * (1 - tx) + lattice[y0 * stride + x0 + 1] * tx;
    const bottom =
      lattice[(y0 + 1) * stride + x0] * (1 - tx) + lattice[(y0 + 1) * stride + x0 + 1] * tx;
    return top * (1 - ty) + bottom * ty;
  };
}

function fbm(random: () => number, octaves: number): (x: number, y: number) => number {
  const layers = Array.from({ length: octaves }, (_, i) => valueNoise(random, 2 << i));
  return (x, y) => {
    let sum = 0;
    let amplitude = 1;
    let total = 0;
    for (const layer of layers) {
      sum += layer(x, y) * amplitude;
      total += amplitude;
      amplitude *= 0.55;
    }
    return sum / total;
  };
}

/** Distance from the centre in 0..1, where 1 is the edge of the tip box. */
function radial(x: number, y: number): number {
  const dx = (x + 0.5) / TIP_SIZE - 0.5;
  const dy = (y + 0.5) / TIP_SIZE - 0.5;
  return Math.hypot(dx, dy) * 2;
}

function paint(fill: (x: number, y: number, u: number, v: number) => number): CoverageBitmap {
  const bitmap = createCoverage(TIP_SIZE, TIP_SIZE);
  for (let y = 0; y < TIP_SIZE; y += 1) {
    for (let x = 0; x < TIP_SIZE; x += 1) {
      const value = fill(x, y, (x + 0.5) / TIP_SIZE, (y + 0.5) / TIP_SIZE);
      bitmap.data[y * TIP_SIZE + x] = Math.round(clamp(value, 0, 1) * 255);
    }
  }
  return bitmap;
}

function square(): CoverageBitmap {
  const edge = 2 / TIP_SIZE;
  return paint((_x, _y, u, v) => {
    const inset = Math.min(u, 1 - u, v, 1 - v);
    return clamp(inset / edge, 0, 1);
  });
}

function star(points = 5): CoverageBitmap {
  return paint((x, y) => {
    const dx = (x + 0.5) / TIP_SIZE - 0.5;
    const dy = (y + 0.5) / TIP_SIZE - 0.5;
    const angle = Math.atan2(dy, dx);
    const distance = Math.hypot(dx, dy) * 2;
    const wave = Math.cos(angle * points) * 0.5 + 0.5;
    const limit = 0.45 + wave * 0.55;
    return clamp((limit - distance) * TIP_SIZE * 0.08, 0, 1);
  });
}

function spatter(): CoverageBitmap {
  const random = makeRandom(0x5eed);
  const blobs = Array.from({ length: 46 }, () => {
    const angle = random() * Math.PI * 2;
    const distance = Math.sqrt(random()) * 0.46;
    return {
      x: 0.5 + Math.cos(angle) * distance,
      y: 0.5 + Math.sin(angle) * distance,
      radius: 0.02 + random() * random() * 0.11,
      strength: 0.55 + random() * 0.45,
    };
  });

  return paint((_x, _y, u, v) => {
    let coverage = 0;
    for (const blob of blobs) {
      const distance = Math.hypot(u - blob.x, v - blob.y) / blob.radius;
      if (distance >= 1) continue;
      const value = blob.strength * (1 - distance * distance);
      coverage = Math.max(coverage, value);
    }
    return coverage;
  });
}

function chalk(): CoverageBitmap {
  const noise = fbm(makeRandom(0xc4a1c), 4);
  return paint((x, y, u, v) => {
    const distance = radial(x, y);
    if (distance >= 1) return 0;
    const body = 1 - distance * distance;
    const grain = noise(u, v);
    return clamp(body * 1.25 - (1 - grain) * 0.95, 0, 1);
  });
}

function charcoal(): CoverageBitmap {
  const noise = fbm(makeRandom(0xc0a1), 5);
  return paint((x, y, u, v) => {
    const distance = radial(x, y);
    const edge = clamp((1 - distance) * 2.4, 0, 1);
    return clamp(edge * (0.35 + noise(u * 1.7, v * 1.7) * 0.9), 0, 1);
  });
}

/** Flat brush: many thin streaks along the major axis, as a dry bristle leaves. */
function bristle(): CoverageBitmap {
  const random = makeRandom(0xb215);
  const streaks = Array.from({ length: 26 }, () => ({
    offset: random(),
    weight: 0.35 + random() * 0.65,
    thickness: 0.008 + random() * 0.02,
  }));

  return paint((x, y, u, v) => {
    const distance = radial(x, y);
    if (distance >= 1) return 0;
    let coverage = 0;
    for (const streak of streaks) {
      const gap = Math.abs(v - streak.offset) / streak.thickness;
      if (gap >= 1) continue;
      coverage = Math.max(coverage, streak.weight * (1 - gap));
    }
    return coverage * clamp((1 - distance) * 3, 0, 1) * clamp(Math.min(u, 1 - u) * 6, 0, 1);
  });
}

/** Heavy grain for texture work: reads as a rough stone or concrete stamp. */
function grunge(): CoverageBitmap {
  const noise = fbm(makeRandom(0x9111), 5);
  const detail = fbm(makeRandom(0x2222), 3);
  return paint((x, y, u, v) => {
    const distance = radial(x, y);
    if (distance >= 1) return 0;
    const mask = clamp((1 - distance) * 1.6, 0, 1);
    const value = noise(u, v) * 0.75 + detail(u * 2.3, v * 2.3) * 0.45;
    return clamp((value - 0.42) * 2.6, 0, 1) * mask;
  });
}

/** Loaded wet bristles: denser streaks with a soft ink bleed. */
function wetBristle(): CoverageBitmap {
  const random = makeRandom(0xe71b);
  const streaks = Array.from({ length: 34 }, () => ({
    offset: 0.12 + random() * 0.76,
    weight: 0.55 + random() * 0.45,
    thickness: 0.012 + random() * 0.028,
  }));
  const bleed = fbm(makeRandom(0xe712), 3);

  return paint((x, y, u, v) => {
    const distance = radial(x, y);
    if (distance >= 1.05) return 0;
    let coverage = 0;
    for (const streak of streaks) {
      const gap = Math.abs(v - streak.offset) / streak.thickness;
      if (gap >= 1) continue;
      coverage = Math.max(coverage, streak.weight * (1 - gap * gap));
    }
    const soft = clamp((1.05 - distance) * 2.2, 0, 1);
    const wash = bleed(u, v) * 0.35 * soft;
    return clamp(coverage * soft * clamp(Math.min(u, 1 - u) * 4.5, 0, 1) + wash, 0, 1);
  });
}

/** Soft felt / marker tip with porous edge. */
function feltTip(): CoverageBitmap {
  const noise = fbm(makeRandom(0xfe17), 4);
  return paint((x, y, u, v) => {
    const distance = radial(x, y);
    if (distance >= 1) return 0;
    const body = clamp((0.92 - distance) * 8, 0, 1);
    const edge = clamp((1 - distance) * 3.5, 0, 1);
    const pores = 0.75 + noise(u * 2.4, v * 2.4) * 0.35;
    return body * 0.85 + edge * pores * 0.35;
  });
}

/** Wide chisel marker face. */
function chisel(): CoverageBitmap {
  return paint((_x, _y, u, v) => {
    const dx = Math.abs(u - 0.5) / 0.42;
    const dy = Math.abs(v - 0.5) / 0.18;
    const box = Math.max(dx, dy);
    if (box >= 1) return 0;
    const edge = 1 - box;
    return clamp(edge * 14, 0, 1);
  });
}

/** Fine graphite grain elongated along the stroke. */
function graphite(): CoverageBitmap {
  const noise = fbm(makeRandom(0x9aaf), 5);
  return paint((x, y, u, v) => {
    const dx = ((x + 0.5) / TIP_SIZE - 0.5) * 2.4;
    const dy = ((y + 0.5) / TIP_SIZE - 0.5) * 3.6;
    const distance = Math.hypot(dx, dy);
    if (distance >= 1) return 0;
    const body = clamp((1 - distance) * 2.8, 0, 1);
    const grain = noise(u * 3.2, v * 3.2);
    return clamp(body * (0.25 + grain * 0.9), 0, 1);
  });
}

/** Waxy crayon with chunky grain. */
function crayon(): CoverageBitmap {
  const noise = fbm(makeRandom(0xc6a7), 4);
  return paint((x, y, u, v) => {
    const distance = radial(x, y);
    if (distance >= 1) return 0;
    const body = clamp((1 - distance * distance) * 1.35, 0, 1);
    const wax = noise(u * 1.4, v * 1.4);
    return clamp(body * (0.4 + wax * 0.75) - (1 - wax) * 0.15, 0, 1);
  });
}

/** Soft pastel dust. */
function pastel(): CoverageBitmap {
  const noise = fbm(makeRandom(0xa57e), 4);
  return paint((x, y, u, v) => {
    const distance = radial(x, y);
    if (distance >= 1) return 0;
    const body = Math.pow(1 - distance, 1.6);
    return clamp(body * (0.55 + noise(u, v) * 0.65), 0, 1);
  });
}

/** Watercolor blot with soft irregular edge. */
function watercolor(): CoverageBitmap {
  const noise = fbm(makeRandom(0xa7e4), 4);
  return paint((x, y, u, v) => {
    const distance = radial(x, y);
    const warp = (noise(u, v) - 0.5) * 0.35;
    const edge = distance + warp;
    if (edge >= 1) return 0;
    const fill = clamp((0.72 - edge) * 3.2, 0, 1);
    const bloom = clamp((1 - edge) * 1.4, 0, 1) * 0.45;
    return clamp(fill * 0.7 + bloom * noise(u * 1.8, v * 1.8), 0, 1);
  });
}

/** Sharp fountain-pen / dip-nib diamond. */
function inkNib(): CoverageBitmap {
  return paint((x, y) => {
    const dx = Math.abs((x + 0.5) / TIP_SIZE - 0.5) * 2.8;
    const dy = Math.abs((y + 0.5) / TIP_SIZE - 0.5) * 2.1;
    const d = dx + dy;
    if (d >= 1) return 0;
    return clamp((1 - d) * 18, 0, 1);
  });
}

/** Very soft airbrush cloud. */
function airbrush(): CoverageBitmap {
  return paint((x, y) => {
    const distance = radial(x, y);
    if (distance >= 1) return 0;
    return Math.pow(1 - distance, 2.4);
  });
}

/** Natural sponge with holes. */
function sponge(): CoverageBitmap {
  const noise = fbm(makeRandom(0x5b0b), 5);
  const holes = fbm(makeRandom(0x501e), 3);
  return paint((x, y, u, v) => {
    const distance = radial(x, y);
    if (distance >= 1) return 0;
    const mask = clamp((1 - distance) * 1.8, 0, 1);
    const body = noise(u * 1.6, v * 1.6);
    const punched = holes(u * 2.8, v * 2.8);
    return clamp((body - 0.28) * 2.2, 0, 1) * mask * clamp(1.15 - punched * 1.1, 0, 1);
  });
}

/** Stipple dots for dry texture. */
function stipple(): CoverageBitmap {
  const random = makeRandom(0x5719);
  const dots = Array.from({ length: 70 }, () => {
    const angle = random() * Math.PI * 2;
    const distance = Math.sqrt(random()) * 0.48;
    return {
      x: 0.5 + Math.cos(angle) * distance,
      y: 0.5 + Math.sin(angle) * distance,
      radius: 0.012 + random() * 0.028,
      strength: 0.5 + random() * 0.5,
    };
  });
  return paint((_x, _y, u, v) => {
    let coverage = 0;
    for (const dot of dots) {
      const distance = Math.hypot(u - dot.x, v - dot.y) / dot.radius;
      if (distance >= 1) continue;
      coverage = Math.max(coverage, dot.strength * (1 - distance));
    }
    return coverage;
  });
}

/** Fan brush: bristles radiating from one end. */
function fan(): CoverageBitmap {
  const random = makeRandom(0xfa21);
  const rays = Array.from({ length: 22 }, () => ({
    angle: -0.55 + random() * 1.1,
    weight: 0.4 + random() * 0.6,
    thickness: 0.01 + random() * 0.018,
  }));
  return paint((x, y) => {
    const u = (x + 0.5) / TIP_SIZE;
    const v = (y + 0.5) / TIP_SIZE;
    const dx = u - 0.15;
    const dy = v - 0.5;
    const dist = Math.hypot(dx, dy);
    if (dist < 0.02 || dist > 0.78) return 0;
    const angle = Math.atan2(dy, dx);
    let coverage = 0;
    for (const ray of rays) {
      const gap = Math.abs(angle - ray.angle) / ray.thickness;
      if (gap >= 1) continue;
      coverage = Math.max(coverage, ray.weight * (1 - gap) * clamp((0.78 - dist) * 2.5, 0, 1));
    }
    return coverage;
  });
}

/** Soft oil flat: wide streaky pad. */
function oilFlat(): CoverageBitmap {
  const random = makeRandom(0x011f);
  const streaks = Array.from({ length: 18 }, () => ({
    offset: 0.2 + random() * 0.6,
    weight: 0.5 + random() * 0.5,
    thickness: 0.02 + random() * 0.04,
  }));
  return paint((x, y, u, v) => {
    const dx = Math.abs(u - 0.5) / 0.48;
    const dy = Math.abs(v - 0.5) / 0.32;
    if (dx >= 1 || dy >= 1) return 0;
    let coverage = 0.25 * clamp(1 - Math.max(dx, dy), 0, 1);
    for (const streak of streaks) {
      const gap = Math.abs(v - streak.offset) / streak.thickness;
      if (gap >= 1) continue;
      coverage = Math.max(coverage, streak.weight * (1 - gap) * clamp(1 - dx, 0, 1));
    }
    const distance = radial(x, y);
    return coverage * clamp((1.05 - distance) * 2, 0, 1);
  });
}

/** Soft ragged dry-rag edge. */
function rag(): CoverageBitmap {
  const noise = fbm(makeRandom(0x6a9), 5);
  return paint((x, y, u, v) => {
    const distance = radial(x, y);
    const tear = (noise(u * 2.2, v * 2.2) - 0.5) * 0.45;
    if (distance + tear >= 1) return 0;
    const body = clamp((0.85 - distance - tear) * 2.4, 0, 1);
    return body * (0.45 + noise(u * 3, v * 3) * 0.55);
  });
}

/** Parallel hatch for dry pencil shading. */
function hatch(): CoverageBitmap {
  const random = makeRandom(0xa7c4);
  const lines = Array.from({ length: 14 }, () => ({
    offset: random(),
    weight: 0.45 + random() * 0.55,
    thickness: 0.006 + random() * 0.012,
  }));
  return paint((x, y, u, v) => {
    const distance = radial(x, y);
    if (distance >= 1) return 0;
    let coverage = 0;
    const t = u * 0.75 + v * 0.25;
    for (const line of lines) {
      const gap = Math.abs(((t + line.offset) % 1) - 0.5) / line.thickness;
      if (gap >= 1) continue;
      coverage = Math.max(coverage, line.weight * (1 - gap));
    }
    return coverage * clamp((1 - distance) * 2.5, 0, 1);
  });
}

/** Pointed brush-pen / sumi tip. */
function brushPen(): CoverageBitmap {
  return paint((x, y) => {
    const u = (x + 0.5) / TIP_SIZE;
    const v = (y + 0.5) / TIP_SIZE;
    const dx = (u - 0.5) * 2.6;
    const dy = (v - 0.22) * 1.55;
    // Teardrop: wider at the base, pointed toward the tip.
    const width = clamp(0.15 + (1 - v) * 0.55, 0.08, 0.7);
    const inside = Math.abs(dx) / width + Math.max(0, dy);
    if (inside >= 1 || v < 0.08 || v > 0.95) return 0;
    return clamp((1 - inside) * 10, 0, 1) * clamp((v - 0.08) * 8, 0, 1);
  });
}

/** Soft spray of tiny droplets. */
function spray(): CoverageBitmap {
  const random = makeRandom(0x59a7);
  const drops = Array.from({ length: 90 }, () => {
    const angle = random() * Math.PI * 2;
    const distance = Math.pow(random(), 0.7) * 0.5;
    return {
      x: 0.5 + Math.cos(angle) * distance,
      y: 0.5 + Math.sin(angle) * distance,
      radius: 0.006 + random() * 0.02,
      strength: 0.35 + random() * 0.65,
    };
  });
  return paint((_x, _y, u, v) => {
    let coverage = 0;
    for (const drop of drops) {
      const distance = Math.hypot(u - drop.x, v - drop.y) / drop.radius;
      if (distance >= 1) continue;
      coverage = Math.max(coverage, drop.strength * (1 - distance * distance));
    }
    return coverage;
  });
}

/** Soft highlighter bar. */
function highlighter(): CoverageBitmap {
  return paint((_x, _y, u, v) => {
    const dx = Math.abs(u - 0.5) / 0.46;
    const dy = Math.abs(v - 0.5) / 0.22;
    const d = Math.hypot(dx * 0.7, dy);
    if (d >= 1) return 0;
    return Math.pow(1 - d, 1.4) * 0.85;
  });
}

/** Conté crayon — denser dry stick. */
function conte(): CoverageBitmap {
  const noise = fbm(makeRandom(0xc027), 5);
  return paint((x, y, u, v) => {
    const dx = ((x + 0.5) / TIP_SIZE - 0.5) * 2.1;
    const dy = ((y + 0.5) / TIP_SIZE - 0.5) * 2.8;
    const distance = Math.hypot(dx, dy);
    if (distance >= 1) return 0;
    const body = clamp((1 - distance) * 2.2, 0, 1);
    return clamp(body * (0.3 + noise(u * 2.5, v * 2.5) * 0.95), 0, 1);
  });
}

/** Soft dual-fibre marker. */
function markerFiber(): CoverageBitmap {
  const noise = fbm(makeRandom(0xf1be), 3);
  return paint((x, y, u, v) => {
    const distance = radial(x, y);
    if (distance >= 1) return 0;
    const core = clamp((0.55 - distance) * 5, 0, 1);
    const halo = clamp((1 - distance) * 1.8, 0, 1) * (0.5 + noise(u, v) * 0.5);
    return clamp(core * 0.9 + halo * 0.55, 0, 1);
  });
}

/** Leaf / foliage stamp. */
function leaf(): CoverageBitmap {
  return paint((x, y) => {
    const u = (x + 0.5) / TIP_SIZE - 0.5;
    const v = (y + 0.5) / TIP_SIZE - 0.5;
    const angle = Math.atan2(v, u);
    const dist = Math.hypot(u, v) * 2.2;
    const lobe = Math.cos(angle * 2) * 0.25 + 0.75;
    const vein = Math.abs(Math.sin(angle * 6)) * 0.12;
    const limit = lobe - vein;
    if (dist >= limit) return 0;
    return clamp((limit - dist) * 8, 0, 1);
  });
}

/** Soft triangle for geometric stamps. */
function triangle(): CoverageBitmap {
  return paint((_x, _y, u, v) => {
    const x = u * 2 - 1;
    const y = v * 2 - 1;
    // Pointing up.
    const edge = Math.max(-y - 0.15, (x * 0.9 + y) * 0.7, (-x * 0.9 + y) * 0.7);
    if (edge >= 0.55) return 0;
    return clamp((0.55 - edge) * 12, 0, 1);
  });
}

function toTipImage(id: string, name: string, bitmap: CoverageBitmap): TipImage {
  return { id, name, width: bitmap.width, height: bitmap.height, data: bitmap.data };
}

/**
 * Sampled tips shipped with the editor. They are generated rather than stored so
 * the bundle stays small, and the PRNG is seeded so they never change.
 */
export function createBuiltinTips(): TipImage[] {
  return [
    toTipImage('tip.square', 'Square', square()),
    toTipImage('tip.spatter', 'Spatter', spatter()),
    toTipImage('tip.chalk', 'Chalk', chalk()),
    toTipImage('tip.charcoal', 'Charcoal', charcoal()),
    toTipImage('tip.bristle', 'Dry Bristle', bristle()),
    toTipImage('tip.wet-bristle', 'Wet Bristle', wetBristle()),
    toTipImage('tip.grunge', 'Grunge', grunge()),
    toTipImage('tip.felt', 'Felt Tip', feltTip()),
    toTipImage('tip.chisel', 'Chisel Marker', chisel()),
    toTipImage('tip.graphite', 'Graphite', graphite()),
    toTipImage('tip.crayon', 'Crayon', crayon()),
    toTipImage('tip.pastel', 'Pastel', pastel()),
    toTipImage('tip.watercolor', 'Watercolor', watercolor()),
    toTipImage('tip.ink-nib', 'Ink Nib', inkNib()),
    toTipImage('tip.airbrush', 'Airbrush', airbrush()),
    toTipImage('tip.sponge', 'Sponge', sponge()),
    toTipImage('tip.stipple', 'Stipple', stipple()),
    toTipImage('tip.fan', 'Fan', fan()),
    toTipImage('tip.oil-flat', 'Oil Flat', oilFlat()),
    toTipImage('tip.rag', 'Dry Rag', rag()),
    toTipImage('tip.hatch', 'Hatch', hatch()),
    toTipImage('tip.brush-pen', 'Brush Pen', brushPen()),
    toTipImage('tip.spray', 'Spray', spray()),
    toTipImage('tip.highlighter', 'Highlighter', highlighter()),
    toTipImage('tip.conte', 'Conte', conte()),
    toTipImage('tip.marker-fiber', 'Marker Fiber', markerFiber()),
    toTipImage('tip.leaf', 'Leaf', leaf()),
    toTipImage('tip.triangle', 'Triangle', triangle()),
    toTipImage('tip.star', 'Star', star()),
  ];
}
